import { memo } from 'react';
import { Activity, BarChart3, Layers3, ShieldCheck, Sparkles, TrendingUp } from 'lucide-react';

import {
  boardFilters,
  type BoardNavigationProps,
} from '@/features/opportunities/opportunityFilterModel';
import type { EnrichedOpportunity } from '@/models/marketData';
import { formatNumber } from '@/utils/formatters';

interface MarketOverviewProps extends BoardNavigationProps {
  opportunities: EnrichedOpportunity[];
  version: string;
}

export const MarketOverview = memo(
  ({ opportunities, activeBoard, onNavigate }: MarketOverviewProps) => {
    const count = opportunities.length;
    const avgScore = opportunities.reduce((sum, item) => sum + item.score, 0) / (count || 1);
    const avgConfidence =
      opportunities.reduce((sum, item) => sum + item.confidence, 0) / (count || 1);
    const values = [
      { label: 'Momentum stocks', value: String(count), hint: 'Ranked candidates', icon: Activity },
      {
        label: 'Average score',
        value: formatNumber(avgScore, 1),
        hint: 'Opportunity strength',
        icon: BarChart3,
      },
      {
        label: 'Avg confidence',
        value: `${formatNumber(avgConfidence, 1)}%`,
        hint: 'Across the radar',
        icon: ShieldCheck,
      },
      {
        label: 'Constructive setups',
        value: String(opportunities.filter(({ signal }) => signal === 5).length),
        hint: 'Highest model signal',
        icon: Sparkles,
      },
      {
        label: 'EMA alignment',
        value: `${opportunities.filter(({ metrics }) => metrics.emaAligned).length}/${count}`,
        hint: 'Close > 20 > 50 > 200',
        icon: Layers3,
      },
      {
        label: 'Bullish SuperTrend',
        value: `${opportunities.filter(({ indicators }) => indicators.superTrendBullish).length}/${count}`,
        hint: 'Trend confirmation',
        icon: TrendingUp,
      },
    ];

    return (
      <section className="kpi-grid" aria-label="Market overview">
        {values.map(({ label, value, hint, icon: Icon }) => {
          const navigable = label in boardFilters;
          const Card = navigable ? 'button' : 'article';
          return (
            <Card
              className={`kpi-card ${navigable ? 'board-filter' : ''}`}
              key={label}
              {...(navigable
                ? {
                    type: 'button' as const,
                    onClick: () => onNavigate(label),
                    'aria-pressed': activeBoard === label,
                    'aria-controls': 'opportunity-radar',
                    'aria-label': `View ${label} stocks`,
                  }
                : {})}
            >
              <div className="kpi-card__top">
                <span>{label}</span>
                <Icon size={16} aria-hidden="true" />
              </div>
              <strong className="kpi-card__value">{value}</strong>
              <small>{hint}</small>
              {navigable && <span className="board-filter__link">View stocks ?</span>}
            </Card>
          );
        })}
      </section>
    );
  },
);
